import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  AcademicCapIcon,
  PlayIcon,
  StopIcon,
  ClockIcon,
  UsersIcon,
  BookOpenIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChartBarIcon,
  EyeIcon,
  ClipboardDocumentListIcon,
  ClipboardDocumentCheckIcon,
} from '@heroicons/react/24/outline'
import { Button } from '@/components/ui/Button'
import { useAuth } from '@/hooks/useAuth'
import { useUserRole } from '@/hooks/useUserRole'
import { getLessons, Lesson } from '@/api/lessonsApi'
import { getTeacherSessions, startSession, endSession, SessionWithDetails } from '@/api/sessionsApi'
import toast from 'react-hot-toast'

export default function TeacherDashboard() {
  const { user } = useAuth()
  const { userRole } = useUserRole()
  const [lessons, setLessons] = useState<Lesson[]>([])
  const [sessions, setSessions] = useState<SessionWithDetails[]>([])
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState<string | null>(null)

  // Stats
  const [stats, setStats] = useState({
    totalLessons: 0,
    activeSessions: 0,
    completedToday: 0,
    totalStudents: 0
  })

  useEffect(() => {
    if (userRole === 'teacher') {
      fetchTeacherData()
    }
  }, [userRole])

  const fetchTeacherData = async () => {
    setLoading(true)
    try {
      console.log('Fetching teacher dashboard data...')
      
      // Fetch teacher's lessons and sessions in parallel
      const [lessonsResponse, sessionsResponse] = await Promise.all([
        getLessons(),
        getTeacherSessions({ page: 1, limit: 50, includeAttendance: false })
      ])

      setLessons(lessonsResponse.data)
      setSessions(sessionsResponse.data.sessions)

      // Calculate stats
      const activeSessions = sessionsResponse.data.sessions.filter(s => s.status === 'in_progress')
      const completedToday = sessionsResponse.data.sessions.filter(s => 
        s.status === 'completed' && 
        new Date(s.actualEndTime || '').toDateString() === new Date().toDateString()
      )

      setStats({
        totalLessons: lessonsResponse.data.length,
        activeSessions: activeSessions.length,
        completedToday: completedToday.length,
        totalStudents: sessionsResponse.data.sessions.reduce((total, session) => 
          total + (session.attendanceCount || 0), 0
        )
      })

    } catch (error) {
      console.error('Failed to fetch teacher data:', error)
      toast.error('Failed to load dashboard data')
    } finally {
      setLoading(false)
    }
  }

  const handleStartSession = async (sessionId: string) => {
    setActionLoading(sessionId)
    try {
      await startSession(sessionId)
      await fetchTeacherData() // Refresh data
      toast.success('Session started successfully!')
    } catch (error) {
      console.error('Failed to start session:', error)
      toast.error('Failed to start session')
    } finally {
      setActionLoading(null)
    }
  }

  const handleEndSession = async (sessionId: string) => {
    setActionLoading(sessionId)
    try {
      await endSession(sessionId, { notes: 'Session completed' })
      await fetchTeacherData() // Refresh data
      toast.success('Session ended successfully!')
    } catch (error) {
      console.error('Failed to end session:', error)
      toast.error('Failed to end session')
    } finally {
      setActionLoading(null)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  const inProgressSessions = sessions.filter(s => s.status === 'in_progress')
  const upcomingSessions = sessions.filter(s => s.status === 'scheduled')
  const recentSessions = sessions.filter(s => s.status === 'completed').slice(0, 5)

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg p-6 text-white">
        <h1 className="text-2xl font-bold">Welcome back, {user?.firstName}!</h1>
        <p className="text-blue-100 mt-2">Ready to inspire and educate your students today?</p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <BookOpenIcon className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">My Lessons</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalLessons}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <PlayIcon className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active Sessions</p>
              <p className="text-2xl font-bold text-gray-900">{stats.activeSessions}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <CheckCircleIcon className="h-8 w-8 text-orange-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Completed Today</p>
              <p className="text-2xl font-bold text-gray-900">{stats.completedToday}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <UsersIcon className="h-8 w-8 text-purple-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Students</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalStudents}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Active Sessions */}
      {inProgressSessions.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <PlayIcon className="h-5 w-5 text-green-600 mr-2" />
              Active Sessions
            </h2>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {inProgressSessions.map((session) => (
                <motion.div
                  key={session.id}
                  className="bg-green-50 border border-green-200 rounded-lg p-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900">{session.title}</h3>
                      <p className="text-sm text-gray-600">{session.description}</p>
                      <div className="flex items-center mt-2 text-xs text-gray-500">
                        <ClockIcon className="h-4 w-4 mr-1" />
                        Started: {new Date(session.actualStartTime || '').toLocaleTimeString()}
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(`/attendance-detail/${session.id}`, '_blank')}
                      >
                        <EyeIcon className="h-4 w-4 mr-1" />
                        View
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        disabled={actionLoading === session.id}
                        onClick={() => handleEndSession(session.id)}
                      >
                        <StopIcon className="h-4 w-4 mr-1" />
                        End Session
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Upcoming Sessions */}
      {upcomingSessions.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <CalendarDaysIcon className="h-5 w-5 text-blue-600 mr-2" />
              Upcoming Sessions
            </h2>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {upcomingSessions.map((session) => (
                <motion.div
                  key={session.id}
                  className="bg-blue-50 border border-blue-200 rounded-lg p-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900">{session.title}</h3>
                      <p className="text-sm text-gray-600">{session.description}</p>
                      <div className="flex items-center mt-2 text-xs text-gray-500">
                        <ClockIcon className="h-4 w-4 mr-1" />
                        Scheduled: {new Date(session.scheduledStartTime).toLocaleString()}
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(`/attendance-detail/${session.id}`, '_blank')}
                      >
                        <EyeIcon className="h-4 w-4 mr-1" />
                        View
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        disabled={actionLoading === session.id}
                        onClick={() => handleStartSession(session.id)}
                      >
                        <PlayIcon className="h-4 w-4 mr-1" />
                        Start Session
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* My Lessons */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <BookOpenIcon className="h-5 w-5 text-purple-600 mr-2" />
            My Lessons
          </h2>
        </div>
        <div className="p-6">
          {lessons.length === 0 ? (
            <div className="text-center py-8">
              <AcademicCapIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No lessons assigned</h3>
              <p className="mt-1 text-sm text-gray-500">Contact your administrator to get lessons assigned.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {lessons.map((lesson) => (
                <motion.div
                  key={lesson.id}
                  className="bg-gray-50 border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-900">{lesson.title}</h3>
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                      {lesson.duration}min
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{lesson.description}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-xs text-gray-500">
                      <UsersIcon className="h-4 w-4 mr-1" />
                      {lesson.streams?.length || 0} stream(s)
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(`/lesson-detail/${lesson.id}`, '_blank')}
                    >
                      <EyeIcon className="h-4 w-4 mr-1" />
                      View
                    </Button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent Sessions */}
      {recentSessions.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <ChartBarIcon className="h-5 w-5 text-gray-600 mr-2" />
              Recent Sessions
            </h2>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {recentSessions.map((session) => (
                <motion.div
                  key={session.id}
                  className="bg-gray-50 border border-gray-200 rounded-lg p-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900">{session.title}</h3>
                      <p className="text-sm text-gray-600">{session.description}</p>
                      <div className="flex items-center mt-2 text-xs text-gray-500">
                        <CheckCircleIcon className="h-4 w-4 mr-1 text-green-600" />
                        Completed: {new Date(session.actualEndTime || '').toLocaleString()}
                        {session.attendanceCount !== undefined && (
                          <>
                            <span className="ml-4">•</span>
                            <UsersIcon className="h-4 w-4 ml-1 mr-1" />
                            {session.presentCount || 0}/{session.attendanceCount || 0} present
                          </>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(`/attendance-detail/${session.id}`, '_blank')}
                    >
                      <EyeIcon className="h-4 w-4 mr-1" />
                      View Attendance
                    </Button>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Quick Actions</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button
              variant="primary"
              onClick={() => window.open('/teacher/lessons', '_blank')}
              className="h-16 flex flex-col items-center justify-center"
            >
              <BookOpenIcon className="h-6 w-6 mb-1" />
              Manage Lessons
            </Button>
            <Button
              variant="secondary"
              onClick={() => window.open('/teacher/sessions', '_blank')}
              className="h-16 flex flex-col items-center justify-center"
            >
              <PlayIcon className="h-6 w-6 mb-1" />
              View All Sessions
            </Button>
            <Button
              variant="outline"
              onClick={() => window.open('/teacher/attendance', '_blank')}
              className="h-16 flex flex-col items-center justify-center"
            >
              <ClipboardDocumentCheckIcon className="h-6 w-6 mb-1" />
              Attendance Tracking
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}